import { Box, Typography, makeStyles } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import clsx from "clsx";
import React from "react";

import { fontFamilies } from "@/designTokens";
import theme from "@/theme";

import { StatusBadge } from "../Badge";

import { useCurrentStepMotionStyles } from "./useCurrentStepMotionStyles";

export type DiagnosisFlowStepState = "completed" | "upcoming" | "plain" | "active";
// 説明の一部だけを太字にするときは、文を区切って bold の部分を指定する
export type DiagnosisFlowStepDescriptionPart = { text: string; bold?: boolean };
export type DiagnosisFlowStep = {
  title: string;
  description: string | readonly DiagnosisFlowStepDescriptionPart[];
  // 項目名の右に出すバッジの文言（例: 「重要」）
  badge?: string;
  state: DiagnosisFlowStepState;
};
const STEP_CIRCLE_SIZE = 28;
const STEP_ROW_PADDING = 12;
const CONNECTOR_WIDTH = 3;

const useStyles = makeStyles(() => ({
  "@keyframes completedStepEnter": {
    from: { transform: "scale(0.9)" },
    "60%": { transform: "scale(1.15)" },
    to: { transform: "scale(1)" },
  },
  "@keyframes completedCheckReveal": {
    from: { clipPath: "inset(0 100% 0 0)", transform: "scale(0.85)" },
    to: { clipPath: "inset(0 0 0 0)", transform: "scale(1)" },
  },
  "@keyframes connectorExtend": {
    from: { clipPath: "inset(0 0 100% 0)" },
    to: { clipPath: "inset(0 0 0 0)" },
  },
  list: {
    display: "flex",
    flexDirection: "column",
    margin: 0,
    padding: "8px 16px",
    listStyle: "none",
    borderRadius: 16,
    backgroundColor: theme.palette.semantic["surface/base"],
  },
  row: {
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: `${STEP_ROW_PADDING}px 0`,
  },
  connector: {
    position: "absolute",
    top: STEP_ROW_PADDING + STEP_CIRCLE_SIZE,
    bottom: -STEP_ROW_PADDING,
    left: (STEP_CIRCLE_SIZE - CONNECTOR_WIDTH) / 2,
    width: CONNECTOR_WIDTH,
    backgroundImage: `radial-gradient(circle, ${theme.palette.semantic["border/strong"]} 1.5px, transparent 1.5px)`,
    backgroundSize: "3px 8px",
    backgroundRepeat: "repeat-y",
    pointerEvents: "none",
  },
  connectorProgress: {
    position: "absolute",
    inset: 0,
    backgroundImage: `radial-gradient(circle, ${theme.palette.primary.main} 1.5px, transparent 1.5px)`,
    backgroundSize: "3px 8px",
    backgroundRepeat: "repeat-y",
  },
  connectorReveal: {
    animation: "$connectorExtend 650ms ease-out both",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  circle: {
    position: "relative",
    zIndex: 1,
    width: STEP_CIRCLE_SIZE,
    height: STEP_CIRCLE_SIZE,
    flexShrink: 0,
    borderRadius: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.semantic["border/default"]}`,
    color: theme.palette.text.secondary,
    fontFamily: fontFamilies.number,
    fontWeight: 600,
    fontSize: 12,
  },
  completed: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderColor: "transparent",
  },
  completedMotion: {
    animation: "$completedStepEnter 450ms ease-out both",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  checkReveal: {
    animation: "$completedCheckReveal 360ms ease-out both",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  active: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderColor: "transparent",
  },
  check: { fontSize: 18 },
  texts: { flex: 1, minWidth: 0 },
  titleRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 },
  badge: { flexShrink: 0 },
  title: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  completedTitle: { color: theme.palette.text.secondary },
  activeTitle: { fontWeight: 700 },
  description: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  currentDescription: { color: theme.palette.text.primary },
}));
interface DiagnosisFlowStepsProps {
  steps: DiagnosisFlowStep[];
  className?: string;
  animateCompletion?: boolean;
  completionDelayMs?: number;
}

const DiagnosisFlowSteps: React.FC<DiagnosisFlowStepsProps> = ({
  steps,
  className,
  animateCompletion = false,
  completionDelayMs = 200,
}) => {
  const classes = useStyles();
  const motionClasses = useCurrentStepMotionStyles();
  // 完了画面では今回終えた最後の1段階だけを動かし、過去の完了は静止させる。
  const lastCompletedIndex = steps.map((step) => step.state).lastIndexOf("completed");
  const isNewlyCompleted = (index: number) => animateCompletion && index === lastCompletedIndex;
  // 次の段階に入るまでは、完了直後の段階も現在と同じ濃さで表示する。
  const activeIndex = steps.findIndex((step) => step.state === "active");
  const highlightedIndex = activeIndex >= 0 ? activeIndex : lastCompletedIndex;
  return (
    <Box component="ol" className={clsx(classes.list, className)} aria-label="診断の流れ">
      {steps.map((step, index) => (
        <Box
          component="li"
          key={step.title}
          className={classes.row}
          aria-current={step.state === "active" ? "step" : undefined}
        >
          {index < steps.length - 1 && (
            <Box component="span" aria-hidden="true" className={classes.connector}>
              {step.state === "completed" && (
                <Box
                  component="span"
                  className={clsx(classes.connectorProgress, isNewlyCompleted(index) && classes.connectorReveal)}
                  // チェックの描画が終わってから、次の番号へ点線を伸ばす。
                  style={isNewlyCompleted(index) ? { animationDelay: `${completionDelayMs + 660}ms` } : undefined}
                />
              )}
            </Box>
          )}
          <Box
            aria-label={step.state === "completed" ? "完了" : undefined}
            className={clsx(
              classes.circle,
              step.state === "completed" && classes.completed,
              isNewlyCompleted(index) && classes.completedMotion,
              step.state === "active" && classes.active,
              step.state === "active" && motionClasses.current
            )}
            style={isNewlyCompleted(index) ? { animationDelay: `${completionDelayMs}ms` } : undefined}
          >
            {step.state === "completed" ? (
              <CheckIcon
                className={clsx(classes.check, isNewlyCompleted(index) && classes.checkReveal)}
                style={isNewlyCompleted(index) ? { animationDelay: `${completionDelayMs + 180}ms` } : undefined}
              />
            ) : (
              index + 1
            )}
          </Box>
          <Box className={classes.texts}>
            <Box className={classes.titleRow}>
              <Typography
                className={clsx(
                  classes.title,
                  step.state === "completed" && index !== highlightedIndex && classes.completedTitle,
                  index === highlightedIndex && classes.activeTitle
                )}
              >
                {step.title}
              </Typography>
              {step.badge && <StatusBadge status="new" label={step.badge} className={classes.badge} />}
            </Box>
            <Typography className={clsx(classes.description, index === highlightedIndex && classes.currentDescription)}>
              {typeof step.description === "string"
                ? step.description
                : step.description.map((part, partIndex) =>
                    part.bold ? (
                      <strong key={partIndex}>{part.text}</strong>
                    ) : (
                      <React.Fragment key={partIndex}>{part.text}</React.Fragment>
                    )
                  )}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
export default DiagnosisFlowSteps;
